/**
 * @module pages/Asset/utils
 * @description Utility functions for asset page rendering and media handling.
 * Provides helpers for slides, video previews, asset metadata, and Vimeo integration.
 */

import React from 'react';
import { IoIosCloudDownload, IoIosPlay } from 'react-icons/io';
import { MdBrokenImage } from 'react-icons/md';
import Image from '@/components/Image';
import BlockContent from '@sanity/block-content-to-react';

/**
 * @func getStoredAssetInfo
 * @summary find given asset in JSON catalog if exists
 * @param {string} assetSlug slug to identify asset
 * @returns {AssetObj | null} given asset or `null`
 */
export function getStoredAssetInfo(assetSlug) {
  try {
    const assets = JSON.parse(window.__assets);
    return assets.find((asset) => asset.slug.current === assetSlug) || null;
  } catch (e) {
    return null;
  }
}

/**
 * @typedef SlideProps
 * @prop {string} preview preview url
 * @prop {string} title title of asset
 * @prop {boolean} [allowDownload=false] toggle ability to preview/download asset (*optional*)
 */

/**
 * @func makeSlide
 * @summary Create image of given asset with potential wrapping link to preview
 * @param {SlideProps} props slide props
 * @returns {ReactNode} markup for above
 */
export const makeSlide = ({ preview, title, allowDownload = true }) =>
  (allowDownload ? (
    <a
      className="rel mb05 height100 film__video-preview block"
      href={`${preview}?dl=${title}`}
    >
      <Image className="x contain" source={`${preview}?w=400`} alt={title} />
    </a>
  ) : (
    <Image className="x contain" source={`${preview}?w=400`} alt={title} />
  ));

/**
 * @func makeRow
 * @param {string|ReactNode} attr attribute
 * @param {string|ReactNode|Array<string>} val attribute value
 *    - if `Array<string>` will return Sanity CMS blocks for given ` val`
 *    - else directly print `val`
 * @returns {ReactNode|null} Markup for table row with attr + val
 */
export const makeRow = (attr, val) => {
  if (!val) return null;

  if (Array.isArray(val)) {
    val = <BlockContent blocks={val} />;
  }

  return (
    <tr>
      <td className="f ais py025">{attr}</td>
      <td className="pl075 py025 ws-pl">{val}</td>
    </tr>
  );
};

/**
 * @typedef MakeVideoPreviewProps
 * @prop {string} preview preview url
 * @prop {string} title asset title
 * @prop {string} vimeoId vimeo video id
 * @prop {function} openModal function to open modal to trigger preview (see `./index.js`)
 */

/**
 * @func makeVideoPreview
 * @summary Creates markup to either display preview slide or broken image if not available
 * @param {MakeVideoPreviewProps} arg0 function args
 * @returns {ReactNode} markup for preview slide **OR** broken image
 */
export const makeVideoPreview = ({ preview, title, vimeoId, openModal }) => (
  <div
    className="rel mb05 film__video-preview link"
    data-micromodal-trigger={`${vimeoId}`}
    onClick={openModal}
  >
    {preview ? (
      makeSlide({ preview, title, allowDownload: false })
    ) : (
      <MdBrokenImage
        className="centerX mt1"
        style={{ height: '100px', width: '100px' }}
      />
    )}
  </div>
);

/**
 * @typedef GetMediaProps
 * @prop {Array<string> | string} previewArray preview url or array of same
 *  - if array, only first preview will be used in generated url
 * @prop {string} vimeoId vimeo video id
 */

/**
 * @callback MediaProps
 * @param {boolean} hasMedia does asset have related media? (preview or vimeoId)
 * @param {boolean} hasVideo has vimeo video id
 * @param {Icon} hoverIcon if preview, use "download" icon, else use "play" icon
 * @param {string} preview preview url
 * @param {Array<string> | string} previewArray preview url or array of same
 * @param {string} vimeoId vimeo video id
 * @param {string | undefined} vimeoSrc vimeo vidoe src + customized query string
 */

/**
 * @func getMedia
 * @summary get asset media metadata from one or more preview strings (though only uses the first in array)
 * @param {GetMediaProps} param0 args
 * @returns {MediaProps} tuple of `MediaProps`
 */
export const getMedia = ({ previewArray, vimeoId }) => {
  const preview = Array.isArray(previewArray) ? previewArray[0] : undefined;
  const hasMedia = preview || vimeoId;
  const hasVideo = vimeoId;
  const hasPreviewOnly = !vimeoId && preview;

  let vimeoSrc;
  let privacyId;
  if (typeof vimeoId === 'string') {
    // If the vimeoId is like: 12345/6789, then the 6789 is the privacyId
    [vimeoId, privacyId] = vimeoId.split('/');

    vimeoSrc = `https://player.vimeo.com/video/${vimeoId}`;
    if (privacyId) {
      vimeoSrc += `?h=${privacyId}&amp;title=0&amp;byline=0&amp;portrait=0&amp;speed=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`;
    } else {
      vimeoSrc += '?loop=false&amp;byline=false&amp;portrait=false&amp;title=false&amp;speed=true&amp;transparent=0&amp;gesture=media';
    }
  }

  const hoverIcon = hasPreviewOnly ? <IoIosCloudDownload /> : <IoIosPlay />;

  return [
    hasMedia,
    hasVideo,
    hoverIcon,
    preview,
    previewArray,
    vimeoId,
    vimeoSrc,
  ];
};

/**
 * @func makeSlides
 * @summary convert array of preview urls into preview slides
 * @param {Array<string> | string} previewArray  preview url or array of same
 * @param {string} title title of given slide
 * @returns {ReactNode|null} array of markup for preview slides or null
 */
export const makeSlides = (previewArray, title) => {
  if (Array.isArray(previewArray) && previewArray.length > 1) {
    return previewArray.map((url) => makeSlide({ preview: url, title }));
  }
  return null;
};
